import { useState, useEffect } from 'react'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import { ethers } from 'ethers'

const FACTORY_ADDRESS = "0x68835E98a1A4e2017969498F3D67f0B66E416816"

function getMetaMaskProvider() {
  if (typeof window === 'undefined' || !(window as any).ethereum) return null;
  const eth = (window as any).ethereum;
  // Multiple providers — MetaMask prefer karo
  if (eth?.providers?.length) {
    const mm = eth.providers.find((p: any) => p.isMetaMask && !p.isPhantom);
    if (mm) return mm;
    // Koi bhi pehla provider lo
    return eth.providers[0];
  }
  // Single provider — jo bhi hai use karo (MetaMask, VD Wallet, Trust etc)
  return eth || null;
}
const CHAIN_ID = 882022
const EXPLORER_URL = 'https://vdscan.io'
const DEX_URL = 'https://swap.vdscan.io'

// Professional ABI & Source Code for History Actions
const DUMMY_ABI = `[{"inputs":[],"stateMutability":"nonpayable","type":"constructor"},{"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"owner","type":"address"},{"indexed":true,"internalType":"address","name":"spender","type":"address"},{"indexed":false,"internalType":"uint256","name":"value","type":"uint256"}],"name":"Approval","type":"event"},{"inputs":[{"internalType":"address","name":"spender","type":"address"},{"internalType":"uint256","name":"amount","type":"uint256"}],"name":"approve","outputs":[{"internalType":"bool","name":"","type":"bool"}],"stateMutability":"nonpayable","type":"function"},{"inputs":[{"internalType":"address","name":"account","type":"address"}],"name":"balanceOf","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},{"inputs":[],"name":"totalSupply","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"}]`;
const DUMMY_CODE = `// SPDX-License-Identifier: MIT\npragma solidity ^0.8.20;\n\nimport "@openzeppelin/contracts/token/ERC20/ERC20.sol";\nimport "@openzeppelin/contracts/token/ERC20/extensions/ERC20Burnable.sol";\nimport "@openzeppelin/contracts/access/Ownable.sol";\n\ncontract VDCToken is ERC20, ERC20Burnable, Ownable {\n    constructor(string memory name, string memory symbol, uint256 supply) ERC20(name, symbol) Ownable(msg.sender) {\n        _mint(msg.sender, supply);\n    }\n}`;

const FACTORY_ABI = [
  {"inputs":[{"internalType":"string","name":"name","type":"string"},{"internalType":"string","name":"symbol","type":"string"},{"internalType":"uint8","name":"decimals","type":"uint8"},{"internalType":"uint256","name":"initialSupply","type":"uint256"},{"internalType":"bool","name":"_isMintable","type":"bool"}],"name":"createToken","outputs":[{"internalType":"address","name":"","type":"address"}],"stateMutability":"payable","type":"function"},
  {"inputs":[],"name":"creationFee","outputs":[{"internalType":"uint256","name":"","type":"uint256"}],"stateMutability":"view","type":"function"},
  {"anonymous":false,"inputs":[{"indexed":true,"internalType":"address","name":"tokenAddress","type":"address"},{"indexed":true,"internalType":"address","name":"creator","type":"address"},{"indexed":false,"internalType":"string","name":"name","type":"string"},{"indexed":false,"internalType":"string","name":"symbol","type":"string"}],"name":"TokenCreated","type":"event"}
]

type Step = 'form' | 'deploying' | 'success'

export default function CreateToken() {
  const [step, setStep] = useState<Step>('form')
  const [wallet, setWallet] = useState('')
  const [deployFee, setDeployFee] = useState('0.5')
  const [result, setResult] = useState<any>(null)
  const [error, setError] = useState('')
  const [showWalletModal, setShowWalletModal] = useState(false)
  const [history, setHistory] = useState<any[]>([])

  const [form, setForm] = useState({
    name: '', symbol: '', totalSupply: '', decimals: '18', mintable: false
  })

  useEffect(() => {
    checkWallet()
    loadHistory()
  }, [])

  const loadHistory = () => {
    const saved = localStorage.getItem('vdc_token_history')
    if (saved) setHistory(JSON.parse(saved))
  }

  const saveToHistory = (data: any) => {
    const newHistory = [data, ...history].slice(0, 10)
    setHistory(newHistory)
    localStorage.setItem('vdc_token_history', JSON.stringify(newHistory))
  }

  // Universal Copy Helper
  const copyToClipboard = async (text: string, msg: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        let textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }
      alert(`Success: ${msg}`);
    } catch (err) {
      console.error('Copy failed', err);
    }
  }

  async function checkWallet() {
    const ethereum = getMetaMaskProvider()
    if (ethereum) {
      const accounts = await ethereum.request({ method: 'eth_accounts' })
      if (accounts[0]) {
        setWallet(accounts[0])
        fetchFee(ethereum)
      }
    }
  }

  async function fetchFee(ethereum: any) {
    try {
      const provider = new ethers.BrowserProvider(ethereum)
      const contract = new ethers.Contract(FACTORY_ADDRESS, FACTORY_ABI, provider)
      const fee = await contract.creationFee()
      setDeployFee(ethers.formatEther(fee))
    } catch (e) { console.error(e) }
  }

  const openDeepLink = (walletType: 'metamask' | 'trust') => {
    const dappUrl = window.location.href.split('//')[1];
    if (walletType === 'metamask') {
      window.location.href = `https://metamask.app.link/dapp/${dappUrl}`;
    } else {
      window.location.href = `https://link.trustwallet.com/open_url?coin=60&url=${window.location.href}`;
    }
  }

  async function connectWallet() {
    const ethereum = getMetaMaskProvider()
    if (!ethereum) {
      setShowWalletModal(true)
      return
    }
    try {
      const accounts = await ethereum.request({ method: 'eth_requestAccounts' })
      setWallet(accounts[0])
      await ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0x' + CHAIN_ID.toString(16) }]
      }).catch(async () => {
        await ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [{
            chainId: '0x' + CHAIN_ID.toString(16),
            chainName: 'Vistara Diamond Chain',
            nativeCurrency: { name: 'VDC', symbol: 'VDC', decimals: 18 },
            rpcUrls: ['https://rpc.vdscan.io'],
            blockExplorerUrls: [EXPLORER_URL]
          }]
        })
      })
      fetchFee(ethereum)
    } catch (e: any) { setError(e.message) }
  }

  async function deployToken() {
    if (!wallet) { connectWallet(); return }
    if (!form.name || !form.symbol || !form.totalSupply) { setError('Fill all required fields'); return }
    setError('')
    
    const ethereum = getMetaMaskProvider()
    try {
      setStep('deploying')
      const provider = new ethers.BrowserProvider(ethereum)
      const signer = await provider.getSigner()
      const factoryContract = new ethers.Contract(FACTORY_ADDRESS, FACTORY_ABI, signer)
      const feeWei = ethers.parseEther(deployFee)

      const tx = await factoryContract.createToken(
        form.name,
        form.symbol.toUpperCase(),
        parseInt(form.decimals),
        BigInt(form.totalSupply),
        form.mintable,
        { 
          value: feeWei,
          gasLimit: 3000000
        }
      )

      const receipt = await tx.wait()
      
      // ✅ FIX: Improved Log Parsing using Interface to find Token Address
      let tokenAddress = ''
      const iface = new ethers.Interface(FACTORY_ABI)
      
      for (const log of receipt.logs) {
        try {
          const parsedLog = iface.parseLog(log)
          if (parsedLog && parsedLog.name === 'TokenCreated') {
            tokenAddress = parsedLog.args[0] // tokenAddress is the first argument
            break
          }
        } catch (e) { continue }
      }

      // Final Fallback if address still not found
      if (!tokenAddress) tokenAddress = "Address Pending... Check Explorer";

      const deployData = {
        name: form.name,
        symbol: form.symbol.toUpperCase(),
        supply: form.totalSupply,
        decimals: form.decimals,
        address: tokenAddress,
        txHash: receipt.hash,
        date: new Date().toLocaleString()
      }

      setResult(deployData)
      saveToHistory(deployData)
      setStep('success')
    } catch (e: any) {
      setStep('form')
      setError(e.reason || e.message)
    }
  }

  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }))

  return (
    <>
      <Header />
      <main className="main-container">
        <div className="content-wrapper">
          
          <div className="hero-section">
            <h1 className="title">🚀 Create Your <span className="gradient-text">Token</span></h1>
            <p className="subtitle">Professional deployment on VDC Chain in under 60 seconds.</p>
          </div>

          {/* Wallet Bar - Mobile Friendly */}
          <div className="wallet-bar">
            <div className="wallet-info">
              <div className={`status-dot ${wallet ? 'active' : ''}`} />
              <span className="address-text">
                {wallet ? `${wallet.slice(0,6)}...${wallet.slice(-4)}` : 'Disconnected'}
              </span>
            </div>
            <div className="wallet-actions">
              <span className="fee-badge">{deployFee} VDC</span>
              <button onClick={connectWallet} className="connect-btn">
                {wallet ? '✓ Connected' : 'Connect'}
              </button>
            </div>
          </div>

          {error && <div className="error-box">⚠️ {error}</div>}

          {/* Step: Form Input */}
          {step === 'form' && (
            <div className="glass-card">
              <h3 className="card-title">📝 Token Configuration</h3>
              <div className="input-grid">
                <div className="input-group">
                  <label>Token Name</label>
                  <input placeholder="e.g. DUBAI JOA" value={form.name} onChange={e => set('name', e.target.value)} />
                </div>
                <div className="input-group">
                  <label>Token Symbol</label>
                  <input placeholder="DJ" value={form.symbol} onChange={e => set('symbol', e.target.value)} maxLength={10} />
                </div>
                <div className="input-group">
                  <label>Initial Supply</label>
                  <input type="number" placeholder="1000000" value={form.totalSupply} onChange={e => set('totalSupply', e.target.value)} />
                </div>
                <div className="input-group">
                  <label>Decimals</label>
                  <input type="number" value={form.decimals} onChange={e => set('decimals', e.target.value)} />
                </div>
              </div>
              <div className="feature-list">
                <div onClick={() => set('mintable', !form.mintable)} className={`feature-item ${form.mintable ? 'active' : ''}`}>
                  <span>🪙 Mintable Feature</span>
                  <div className="check">{form.mintable ? '✓' : ''}</div>
                </div>
              </div>
              <button onClick={deployToken} className="deploy-btn">🚀 Create Token</button>
            </div>
          )}

          {/* Step: Processing */}
          {step === 'deploying' && (
            <div className="loading-state">
              <div className="loader" />
              <h2>Processing Token...</h2>
              <p>Please confirm the transaction in your wallet</p>
              <p className="tiny-text">Blockchain confirmation may take 5-10 seconds</p>
            </div>
          )}

          {/* Step: Success Display */}
          {step === 'success' && result && (
            <div className="success-card">
              <div className="success-icon">🎉</div>
              <h2 className="success-title">Token Successfully Live!</h2>
              
              <div className="overview-box">
                <div className="ov-item"><span>Name:</span> <strong>{result.name}</strong></div>
                <div className="ov-item"><span>Symbol:</span> <strong>{result.symbol}</strong></div>
                <div className="ov-item"><span>Supply:</span> <strong>{Number(result.supply).toLocaleString()}</strong></div>
                <div className="ov-item"><span>Decimals:</span> <strong>{result.decimals}</strong></div>
              </div>

              <div className="result-links">
                <div className="link-item">
                  <label>New Contract Address</label>
                  <div className="copy-row">
                    <span>{result.address}</span>
                    <button onClick={() => copyToClipboard(result.address, "Address Copied!")}>📋</button>
                  </div>
                </div>
              </div>

              <div className="action-row">
                <a href={`${EXPLORER_URL}/token/${result.address}`} target="_blank" rel="noreferrer" className="outline-btn">🔍 Explorer</a>
                <a href={`${DEX_URL}/#/add/${result.address}`} target="_blank" rel="noreferrer" className="primary-btn">💧 Add Liquidity</a>
              </div>
              <button onClick={() => setStep('form')} className="back-btn">← Deploy Another Token</button>
            </div>
          )}

          {/* Recent History Section */}
          {history.length > 0 && (
            <div className="history-section">
              <h3 className="section-title">🕒 Your Created Tokens</h3>
              {history.map((item, i) => (
                <div key={i} className="history-card">
                  <div className="history-header">
                    <strong>{item.name} ({item.symbol})</strong>
                    <span>{item.date}</span>
                  </div>
                  <div className="history-addr">{item.address.slice(0,25)}...</div>
                  <div className="history-actions">
                    <button onClick={() => copyToClipboard(DUMMY_ABI, "ABI Copied!")}>Copy ABI</button>
                    <button onClick={() => copyToClipboard(DUMMY_CODE, "Source Code Copied!")}>Copy Code</button>
                    <a href={`${EXPLORER_URL}/token/${item.address}`} target="_blank" rel="noreferrer">View</a>
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>

        {/* Improved Wallet Bottom Sheet */}
        {showWalletModal && (
          <div className="modal-overlay" onClick={() => setShowWalletModal(false)}>
            <div className="bottom-sheet" onClick={e => e.stopPropagation()}>
              <div className="sheet-handle" />
              <h3>Connect to VDC Tools</h3>
              <p>Select a wallet to access Vistara Diamond Chain</p>
              <div className="wallet-options">
                <div className="wallet-option" onClick={() => openDeepLink('metamask')}>
                  <div className="icon-circle">
                    <img src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTGv9mGuvPQ9e7VoqfrfeO8Y1vdvPzB7ssGomSn3YMw6Q&s" alt="MetaMask" />
                  </div>
                  <span>MetaMask</span>
                </div>
                <div className="wallet-option" onClick={() => openDeepLink('trust')}>
                  <div className="icon-circle">
                    <img src="https://trustwallet.com/assets/images/media/assets/TWT.png" alt="Trust" />
                  </div>
                  <span>Trust Wallet</span>
                </div>
              </div>
              <button className="close-sheet" onClick={() => setShowWalletModal(false)}>Cancel</button>
            </div>
          </div>
        )}
      </main>
      <Footer />

      <style jsx>{`
        .main-container { min-height: 100vh; padding: 100px 20px; background: #0b0e11; color: white; font-family: 'Inter', sans-serif; }
        .content-wrapper { max-width: 700px; margin: 0 auto; }
        .hero-section { text-align: center; margin-bottom: 40px; }
        .title { font-size: clamp(24px, 5vw, 36px); font-weight: 800; margin-bottom: 10px; letter-spacing: -0.5px; }
        .gradient-text { background: linear-gradient(90deg, #7c3aed, #06b6d4); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
        .subtitle { color: #94a3b8; font-size: 15px; }

        .wallet-bar { display: flex; align-items: center; justify-content: space-between; background: #1a1d23; padding: 14px 20px; border-radius: 18px; border: 1px solid #2d3139; margin-bottom: 30px; box-shadow: 0 4px 20px rgba(0,0,0,0.2); }
        .wallet-info { display: flex; align-items: center; gap: 10px; }
        .status-dot { width: 10px; height: 10px; border-radius: 50%; background: #ef4444; }
        .status-dot.active { background: #22c55e; box-shadow: 0 0 10px #22c55e; }
        .address-text { font-size: 14px; color: #e2e8f0; font-weight: 500; }
        .wallet-actions { display: flex; align-items: center; gap: 12px; }
        .fee-badge { color: #a78bfa; font-weight: 800; font-size: 14px; background: rgba(124,58,237,0.1); padding: 5px 10px; border-radius: 8px; }
        .connect-btn { background: linear-gradient(135deg, #7c3aed, #06b6d4); border: none; padding: 10px 18px; border-radius: 12px; color: white; font-weight: 700; cursor: pointer; font-size: 13px; transition: 0.3s; }
        .connect-btn:hover { opacity: 0.9; transform: translateY(-1px); }

        .glass-card { background: #161a1e; border: 1px solid #2d3139; padding: 25px; border-radius: 24px; box-shadow: 0 10px 30px rgba(0,0,0,0.3); }
        .input-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 20px; }
        @media (max-width: 500px) { .input-grid { grid-template-columns: 1fr; } .wallet-bar { padding: 12px 15px; } .address-text { font-size: 12px; } }
        
        .input-group label { display: block; font-size: 12px; color: #94a3b8; margin-bottom: 6px; font-weight: 600; text-transform: uppercase; }
        .input-group input { width: 100%; background: #0b0e11; border: 1px solid #2d3139; padding: 14px; border-radius: 14px; color: white; outline: none; font-size: 14px; transition: 0.3s; }
        .input-group input:focus { border-color: #7c3aed; background: #161a1e; }
        
        .feature-item { display: flex; justify-content: space-between; padding: 16px; background: #1f2329; border-radius: 14px; cursor: pointer; margin-bottom: 20px; border: 1px solid #2d3139; transition: 0.2s; }
        .feature-item.active { border-color: #7c3aed; background: rgba(124, 58, 237, 0.05); }
        .feature-item span { font-weight: 600; font-size: 14px; }
        .check { width: 22px; height: 22px; background: #2d3139; border-radius: 6px; display: flex; align-items: center; justify-content: center; font-weight: 800; color: #7c3aed; }
        .feature-item.active .check { background: #7c3aed; color: white; }

        .deploy-btn { width: 100%; padding: 18px; border-radius: 14px; border: none; background: linear-gradient(90deg, #7c3aed, #06b6d4); color: white; font-weight: 800; font-size: 16px; cursor: pointer; box-shadow: 0 5px 15px rgba(124,58,237,0.3); }

        .success-card { text-align: center; background: #161a1e; padding: 35px; border-radius: 28px; border: 1px solid rgba(34,197,94,0.3); }
        .success-icon { font-size: 60px; margin-bottom: 15px; }
        .overview-box { background: #0b0e11; padding: 20px; border-radius: 20px; margin: 20px 0; display: grid; grid-template-columns: 1fr 1fr; gap: 12px; text-align: left; }
        .ov-item { display: flex; flex-direction: column; gap: 2px; }
        .ov-item span { color: #64748b; font-size: 11px; text-transform: uppercase; }
        .ov-item strong { color: #e2e8f0; font-size: 15px; }
        
        .copy-row { display: flex; justify-content: space-between; background: #0b0e11; padding: 14px; border-radius: 12px; font-family: 'JetBrains Mono', monospace; font-size: 12px; color: #a78bfa; border: 1px solid #2d3139; margin-top: 5px; }
        .copy-row button { background: none; border: none; color: #64748b; cursor: pointer; font-size: 16px; padding: 0 5px; }
        
        .action-row { display: flex; gap: 12px; margin-bottom: 20px; }
        .primary-btn { flex: 1; background: white; color: black; padding: 14px; border-radius: 12px; font-weight: 700; text-decoration: none; font-size: 14px; display: flex; align-items: center; justify-content: center; }
        .outline-btn { flex: 1; border: 1px solid #2d3139; color: white; padding: 14px; border-radius: 12px; text-decoration: none; font-size: 14px; display: flex; align-items: center; justify-content: center; }
        .back-btn { background: none; border: none; color: #94a3b8; font-size: 13px; cursor: pointer; }

        .history-section { margin-top: 50px; }
        .history-card { background: #1a1d23; padding: 18px; border-radius: 20px; margin-bottom: 16px; border: 1px solid #2d3139; transition: 0.3s; }
        .history-card:hover { border-color: #7c3aed; transform: translateY(-2px); }
        .history-header { display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 14px; color: #e2e8f0; }
        .history-addr { font-family: monospace; color: #64748b; font-size: 12px; margin-bottom: 15px; }
        .history-actions { display: flex; gap: 8px; }
        .history-actions button, .history-actions a { background: #2d3139; border: 1px solid #3d4149; color: #94a3b8; padding: 6px 12px; border-radius: 8px; cursor: pointer; font-size: 11px; text-decoration: none; font-weight: 600; transition: 0.2s; }
        .history-actions button:hover { background: #7c3aed; color: white; border-color: #7c3aed; }

        .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.85); display: flex; align-items: flex-end; z-index: 9999; backdrop-filter: blur(5px); }
        .bottom-sheet { width: 100%; background: #161a1e; border-radius: 32px 32px 0 0; padding: 30px; text-align: center; animation: slideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1); border-top: 1px solid #2d3139; }
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        .sheet-handle { width: 45px; height: 5px; background: #2d3139; border-radius: 10px; margin: 0 auto 25px; }
        .wallet-options { display: flex; gap: 30px; justify-content: center; margin: 35px 0; }
        .wallet-option { cursor: pointer; display: flex; flex-direction: column; align-items: center; gap: 10px; }
        .icon-circle { width: 64px; height: 64px; background: #1f2329; border-radius: 18px; display: flex; align-items: center; justify-content: center; border: 1px solid #2d3139; transition: 0.2s; }
        .wallet-option:hover .icon-circle { transform: scale(1.1); border-color: #7c3aed; }
        .icon-circle img { width: 40px; height: 40px; object-fit: contain; }
        .wallet-option span { font-size: 13px; font-weight: 600; color: #94a3b8; }
        .close-sheet { background: #2d3139; border: none; color: #ef4444; font-weight: 700; cursor: pointer; padding: 12px 30px; border-radius: 12px; margin-top: 10px; }
        
        .loading-state { text-align: center; padding: 60px 0; }
        .loader { width: 50px; height: 50px; border: 5px solid rgba(124,58,237,0.2); border-top-color: #7c3aed; border-radius: 50%; animation: spin 0.8s linear infinite; margin: 0 auto 25px; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .tiny-text { color: #64748b; font-size: 12px; margin-top: 10px; }
        .error-box { background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.2); border-radius: 15px; padding: 15px; color: #f87171; font-size: 13px; margin-bottom: 20px; }
      `}</style>
    </>
  )
}